import React, { useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Invoice } from '../../types';
import { Button } from '../../components/ui/Button';
import { useData } from '../../store/DataContext';
import { IssuerHeader } from './IssuerHeader';
import { calculateInvoicePreTaxLine, calculateInvoiceTotals, formatCurrency } from './utils';
type Props = {
  invoice: Invoice;
  onClose: () => void;
};
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}
export function InvoiceModal({
  invoice,
  onClose
}: Props) {
  const {
    patients,
    walkInClients
  } = useData();
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const patient = invoice.customerType === 'Patient' ? patients.find(entry => entry.id === invoice.customerId) : undefined;
  const walkIn = invoice.customerType === 'WalkIn' ? walkInClients.find(entry => entry.id === invoice.customerId) : undefined;
  const totals = calculateInvoiceTotals(invoice.items);
  const print = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => document.body.classList.remove('printing-test-modal'), 500);
  };
  const downloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${invoice.invoiceNumber}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" data-id="element-1250" data-mp="3rt8s17hu4nbv">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-1251" data-mp="3dtg5lodcct9p">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-1252" data-mp="34u2smlq0dmk8">
          <h3 className="text-lg font-semibold text-slate-900" data-id="element-1253" data-mp="38wpf073wufa1">
            Invoice {invoice.invoiceNumber}
          </h3>
          <div className="flex items-center gap-2" data-id="element-1254" data-mp="1739kv08m6iab">
            <Button variant="outline" size="sm" onClick={print} data-id="element-1255" data-mp="172q43mup9zxv">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-1256" data-mp="id5bj7ggay32" /> Print
            </Button>
            <Button size="sm" onClick={downloadPdf} disabled={isGeneratingPdf} data-id="element-1257" data-mp="ddiwcsh2b7rm">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-1258" data-mp="dboczilfvnpn" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close invoice" data-id="element-1259" data-mp="3g4in0miwx30">
              <XIcon className="h-5 w-5" data-id="element-1260" data-mp="3qqkmgjih7eyb" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-1261" data-mp="3lr84ac2juw22">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-1262" data-mp="3bv4ymw8hofad">
            <IssuerHeader data-id="element-1263" data-mp="37um6t26lj986" />
            <div className="text-left sm:text-right" data-id="element-1264" data-mp="37u2xg9v0ht5r">
              <h1 className="text-2xl font-bold tracking-[0.16em] text-slate-800 sm:text-3xl" data-id="element-1265" data-mp="1nc1ytpbsli2d">
                INVOICE
              </h1>
              <p className="mt-3" data-id="element-1266" data-mp="1rbj52kz1mj8l">
                <span className="font-semibold" data-id="element-1267" data-mp="1wc75fx95wcfh">Invoice #:</span>{' '}
                {invoice.invoiceNumber}
              </p>
              <p data-id="element-1268" data-mp="21btervlvd8j0">
                <span className="font-semibold" data-id="element-1269" data-mp="21a8ju4vn7nkm">Date:</span>{' '}
                {new Date(invoice.date).toLocaleDateString()}
              </p>
              <p data-id="element-1270" data-mp="13lote6qgn0px">
                <span className="font-semibold" data-id="element-1271" data-mp="18mcrtdh0qju6">Due:</span>{' '}
                {new Date(invoice.dueDate).toLocaleDateString()}
              </p>
            </div>
          </header>

          <section className="my-8 flex justify-between gap-8" data-id="element-1272" data-mp="1djw5ab5w6sl2">
            <div data-id="element-1273" data-mp="1ie0ez26jlepu">
              <h3 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1274" data-mp="1ieiimjvjav7s">
                Bill To
              </h3>
              {invoice.customerType === 'Patient' ? <>
                  <p className="text-lg font-bold" data-id="element-1275" data-mp="puaadzwjyziv">
                    {patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient'}
                  </p>
                  {patient && <>
                      <p data-id="element-1276" data-mp="rjx81asnzmpf">File No: {patient.bmcFileNumber}</p>
                      <p data-id="element-1277" data-mp="mmf8uc4g8lum">{patient.address}</p>
                      <p data-id="element-1278" data-mp="mmp00318cz1w">{patient.contact}</p>
                    </>}
                </> : <>
                  <p className="text-lg font-bold" data-id="element-1279" data-mp="3r0dfuz6p4dmt">
                    {invoice.customerName || walkIn?.name || 'Walk-in client'}
                  </p>
                  {walkIn && <>
                      <p data-id="element-1280" data-mp="3vxl6lyh4gg4">{walkIn.address}</p>
                      <p data-id="element-1281" data-mp="3u2xx0zqcc8v">{walkIn.phone}</p>
                    </>}
                </>}
            </div>
            <div className="text-right" data-id="element-1282" data-mp="1fat7mu3d3m78">
              <h3 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1283" data-mp="1f9rkhr92n995">
                Payment Status
              </h3>
              <p className="text-xl font-bold uppercase" data-id="element-1284" data-mp="2x6wcmm1zmutr">{invoice.status}</p>
              <p className="mt-2" data-id="element-1285" data-mp="328lmvzhdz4gs">
                <span className="font-semibold" data-id="element-1286" data-mp="325fy6lco64ll">Amount Paid:</span>{' '}
                {formatCurrency(invoice.amountPaid)}
              </p>
              <p className="text-red-600" data-id="element-1287" data-mp="2t6cd5btxzf4g">
                <span className="font-semibold" data-id="element-1288" data-mp="2t7dzicgero1h">Balance Due:</span>{' '}
                {formatCurrency(invoice.balanceDue)}
              </p>
            </div>
          </section>

          <div className="overflow-x-auto" data-id="element-1289" data-mp="3kwz34xq97sq9">
            <table className="mb-8 min-w-[760px] w-full border-collapse" data-id="element-1290" data-mp="3kzuws9v2jv8w">
              <thead data-id="element-1291" data-mp="376desti16qkz">
                <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1292" data-mp="374surxtceq1d">
                  <th className="p-2.5 text-left font-bold uppercase text-slate-600" data-id="element-1293" data-mp="3c3vqgrnfnjwo">
                    Service
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1294" data-mp="8gn3rec0clj5">
                    Unit Price
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1295" data-mp="24kjnnvxf4jf8">
                    Discount
                  </th>
                  <th className="p-2.5 text-right font-bold uppercase text-slate-600" data-id="element-1296" data-mp="1zr8dplxb7mj3">
                    Pre-tax Total
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1297" data-mp="1pwoteb3ri701">
                {invoice.items.map(item => {
                const line = calculateInvoicePreTaxLine(item);
                return <tr key={item.id} className="border-b border-slate-200" data-id="element-1298" data-mp="2ikknd81y5kt4">
                      <td className="p-2.5" data-id="element-1299" data-mp="2djwyot16xg22">
                        <p className="font-semibold" data-id="element-1300" data-mp="2divdvwudhf75">{item.serviceName}</p>
                        <p className="mt-0.5 text-xs text-slate-500" data-id="element-1301" data-mp="29htar4xc4izw">
                          Qty {item.quantity}
                          {item.description ? ` · ${item.description}` : ''}
                        </p>
                      </td>
                      <td className="p-2.5 text-right" data-id="element-1302" data-mp="y62995u4snm5">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="p-2.5 text-right" data-id="element-1303" data-mp="3drv261cc4jtd">
                        {item.discount}%
                        <span className="block text-xs text-red-600" data-id="element-1304" data-mp="3irhdg58lrpzj">
                          -{formatCurrency(line.discountAmount)}
                        </span>
                      </td>
                      <td className="p-2.5 text-right font-semibold" data-id="element-1305" data-mp="3si2bnrkd6shx">
                        {formatCurrency(line.total)}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between gap-8" data-id="element-1306" data-mp="5b091dlcadsh">
            <div className="max-w-md flex-1" data-id="element-1307" data-mp="aauvn4y30h7q">
              {invoice.notes && <>
                  <h4 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1308" data-mp="275hm1b3w81kh">
                    Notes
                  </h4>
                  <p className="whitespace-pre-wrap text-slate-700" data-id="element-1309" data-mp="2c3j35qhrdqtb">
                    {invoice.notes}
                  </p>
                </>}
              {invoice.paymentTerms && <div className="mt-5" data-id="element-1310" data-mp="1hc0dxy1db3o6">
                  <h4 className="mb-2 font-bold uppercase text-slate-500" data-id="element-1311" data-mp="1m8hbtjydcweg">
                    Terms
                  </h4>
                  <p className="text-slate-700" data-id="element-1312" data-mp="1r9556ca2lpbu">{invoice.paymentTerms}</p>
                </div>}
            </div>
            <dl className="w-full max-w-xs" data-id="element-1313" data-mp="1v9f97jeay5o2">
              <div className="flex justify-between py-1" data-id="element-1314" data-mp="2089yn46rysh5">
                <dt className="text-slate-600" data-id="element-1315" data-mp="206p3pdgjt7ir">Subtotal:</dt>
                <dd className="font-medium" data-id="element-1316" data-mp="250jmmeobxigs">
                  {formatCurrency(totals.subtotal)}
                </dd>
              </div>
              {totals.totalDiscount > 0 && <div className="flex justify-between py-1" data-id="element-1317" data-mp="2eyh8ivipv368">
                  <dt className="text-slate-600" data-id="element-1318" data-mp="2evv0yvvtsno0">Discount:</dt>
                  <dd className="font-medium text-red-600" data-id="element-1319" data-mp="2j02ur7h54tt3">
                    -{formatCurrency(totals.totalDiscount)}
                  </dd>
                </div>}
              <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1320" data-mp="3a2wmdufqj1k">
                <dt className="text-slate-600" data-id="element-1321" data-mp="3ug4123jwaee8">TOT Tax (5%):</dt>
                <dd className="font-medium" data-id="element-1322" data-mp="3uiqbqc231a0o">
                  {formatCurrency(totals.totalTax)}
                </dd>
              </div>
              <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1323" data-mp="r0f2lj64e5pi">
                <dt data-id="element-1324" data-mp="m0u397n1fvc9">Total:</dt>
                <dd className="text-blue-700" data-id="element-1325" data-mp="m0kd9k1zto6k">
                  {formatCurrency(totals.totalAmount)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>;
}